import {Component, Inject, OnInit, ChangeDetectorRef, ChangeDetectionStrategy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl, FormGroup} from '@angular/forms';
import {ItemDataService} from '@emm/common-items/services/item-data.service';
import {VariationAssignment} from '@emm/common-items/models/variation.model';

@Component({
  selector: 'emm-create-assignment-dialog',
  templateUrl: './create-or-edit-assignment-dialog.component.html',
  styleUrls: ['./create-or-edit-assignment-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateOrEditAssignmentDialogComponent implements OnInit{

    form: FormGroup;
    locationOptions: { value: string; label: string }[] = [];
    availableFields: { key: string; label: string; rank: number }[] = [];
    activeFields: string[] = [];
    fieldOptions: { [key: string]: string[] } = {};
    private labelMap: { [key: string]: string } = {};
    private rankMap: { [key: string]: number } = {};
    private activeFieldsSet = new Set<string>();
    isEditMode = false;
    dialogTitle = 'Create Assignment';

    constructor(
        public dialogRef: MatDialogRef<CreateOrEditAssignmentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {locationId: string, mode?: 'create' | 'edit', assignment?: VariationAssignment},
        private cdr: ChangeDetectorRef,
        private itemDataService: ItemDataService,
    ) {
        this.isEditMode = data?.mode === 'edit';
        this.dialogTitle = this.isEditMode ? 'Edit Assignment' : 'Create Assignment';
    }

    ngOnInit() {
        this.buildForm();
        this.itemDataService.getAllowedAssignmentProperties().subscribe(propertyDetails => {
            if (propertyDetails && Array.isArray(propertyDetails)) {
                const fields: { key: string; label: string; rank: number }[] = [];
                propertyDetails.forEach((property: any) => {
                    const key = property.propertyName;
                    const label = this.formatPropertyLabel(key);
                    const rank = property.propertyRank;
                    fields.push({ key, label, rank });
                    this.labelMap[key] = label;
                    this.rankMap[key] = rank;
                    this.fieldOptions[key] = property.availableValues || [];
                });
                this.availableFields = fields.sort((a, b) => b.rank - a.rank);
                if (this.isEditMode && this.data.assignment) {
                    this.populateFormFromAssignment();
                }
                this.cdr.markForCheck();
            }
        });
    }

    private formatPropertyLabel(propertyName: string): string {
        return propertyName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    private populateFormFromAssignment() {
        const assignment = this.data.assignment;
        if (!assignment || !assignment.properties) {
            return;
        }
        const properties = assignment.properties;
        const propertyKeys = Object.keys(properties);
        propertyKeys.forEach(key => {
            if (!this.activeFieldsSet.has(key)) {
                this.activeFieldsSet.add(key);
                this.form.addControl(key, new FormControl(properties[key]));
            } else {
                this.form.get(key)?.setValue(properties[key]);
            }
        });
        this.activeFields = Array.from(this.activeFieldsSet).sort((a, b) =>
            (this.rankMap[b] || 0) - (this.rankMap[a] || 0)
        );

        this.cdr.markForCheck();
    }

    buildForm() {
        const assignment = this.isEditMode && this.data?.assignment ? this.data.assignment : null;
        const locationId = assignment?.locationId || this.data?.locationId || 'CHAIN';
        const consumer = assignment?.consumer || 'COMMERCEPOINT';

        this.locationOptions = [
            { value: 'CHAIN', label: 'Chain Wide Defaults' }
        ];

        if (locationId !== 'CHAIN') {
            const locationLabels: { [key: string]: string } = {
                US: 'United States',
                CA: 'Canada',
                PR: 'Puerto Rico'
            };
            this.locationOptions.push({
                value: locationId,
                label: locationLabels[locationId] || locationId
            });
        }

        this.form = new FormGroup({
            consumer: new FormControl({ value: consumer, disabled: true }),
            location: new FormControl({ value: locationId, disabled: true })
        })
    }

    updateFields(selectedFields: string[]) {
        const nextSet = new Set<string>(selectedFields);
        selectedFields.forEach(fieldKey => {
            if (!this.activeFieldsSet.has(fieldKey)) {
                this.form.addControl(fieldKey, new FormControl(''));
            }
        });
        this.activeFieldsSet.forEach(fieldKey => {
            if (!nextSet.has(fieldKey)) {
                this.form.removeControl(fieldKey);
            }
        });
        this.activeFieldsSet = nextSet;
        this.activeFields = selectedFields.slice().sort((a, b) => (this.rankMap[b] || 0) - (this.rankMap[a] || 0));
        this.cdr.markForCheck();
    }

    removeField(fieldKey: string) {
        if (this.activeFieldsSet.has(fieldKey)) {
            this.activeFieldsSet.delete(fieldKey);
            this.form.removeControl(fieldKey);
            this.activeFields = this.activeFields.filter(k => k !== fieldKey);
            this.cdr.markForCheck();
        }
    }

    getFieldLabel(fieldKey: string): string {
        return this.labelMap[fieldKey] || fieldKey;
    }

    getFieldOptions(fieldKey: string): string[] {
        return this.fieldOptions[fieldKey] || [];
    }

    close(): void {
        this.dialogRef.close();
    }

    save() {
        if (this.form.valid) {
            this.dialogRef.close(this.form.getRawValue());
        }
    }
}
